/** Subscriptions tab — admin dashboard for user subscription analytics.
 *
 * Renders KPI cards (total users, trial users, expired trials, subscribed)
 * and a plan-distribution bar chart. Data comes from the plan-distribution
 * metrics endpoint.
 */

import { useMemo, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertCircle,
  Crown,
  RefreshCw,
  UserCheck,
  UserX,
  Users,
  type LucideIcon,
} from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { T } from "@/shared/ui/DesignLib";
import { DateRangePicker } from "@/shared/ui/date-range-picker";

import { useSubscriptions } from "../hooks/useSubscriptions";
import {
    dateRangeToMetricsRange,
    isCustomDateRange,
    metricsRangeToDateRange,
    type MetricsRange,
    type TimeRange,
} from "../lib/metricsTypes";
import MetricCard from "./MetricCard";

const RANGE_LABELS: Record<TimeRange, string> = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
};

function rangeLabel(range: MetricsRange): string {
  if (isCustomDateRange(range)) return `${range.from} – ${range.to}`;
  return RANGE_LABELS[range];
}

const PLAN_BAR_COLORS = [T.primary, T.purple, T.amber, T.green, "#38bdf8", "#fb7185"];

const AXIS_STYLE = {
  fontSize: 10,
  fontFamily: "var(--font-mono)",
  fill: "var(--muted-foreground)",
} as const;

const TOOLTIP_STYLE = {
  background: "var(--card)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  color: "var(--foreground)",
} as const;

const TOOLTIP_ITEM_STYLE = {
  color: "var(--foreground)",
} as const;

const TOOLTIP_LABEL_STYLE = {
  color: "var(--muted-foreground)",
} as const;

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: readonly any[]; label?: string | number }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={TOOLTIP_STYLE}>
      {label && <p style={{ ...TOOLTIP_LABEL_STYLE, margin: 0, paddingBottom: 4 }}>{label}</p>}
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {payload.map((item, i) => (
          <li key={i} style={{ ...TOOLTIP_ITEM_STYLE, display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: item.color ?? "var(--foreground)",
                flexShrink: 0,
              }}
            />
            <span>
              {item.name ?? item.dataKey} : {item.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SectionCard({
  title,
  subtitle,
  icon: Icon,
  children,
}: {
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Icon className="size-4 shrink-0 text-primary" aria-hidden="true" />
          <CardTitle className="text-sm font-semibold text-foreground">
            {title}
          </CardTitle>
        </div>
        {subtitle && (
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {subtitle}
          </p>
        )}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-[180px] items-center justify-center rounded-[var(--radius-sm)] border border-dashed border-border">
      <p className="max-w-sm text-center text-[12px] text-muted-foreground">
        {message}
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} height={104} borderRadius={8} data-testid="subscriptions-skeleton" />
        ))}
      </div>
      <Skeleton height={320} borderRadius={8} data-testid="subscriptions-skeleton" />
      <Skeleton height={280} borderRadius={8} data-testid="subscriptions-skeleton" />
    </div>
  );
}

export interface SubscriptionsTabProps {
  className?: string;
}

export default function SubscriptionsTab({ className }: SubscriptionsTabProps) {
  const {
    planDistribution,
    loading,
    error,
    range,
    setRange,
    refetch,
  } = useSubscriptions("30d");

  const planData = useMemo(
    () =>
      (planDistribution?.plans ?? []).map((p) => ({
        ...p,
        label: p.label,
      })),
    [planDistribution],
  );

  return (
    <div className={className}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1
            className="m-0 text-[1.5rem] font-bold leading-[1.1] text-foreground md:text-[2rem]"
            style={{ fontFamily: T.serif }}
          >
            Subscriptions
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {rangeLabel(range)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker
            value={metricsRangeToDateRange(range)}
            onChange={(dr) => {
              const next = dateRangeToMetricsRange(dr);
              setRange(next ?? "30d");
            }}
            align="end"
            maxDate={new Date()}
          />
          <Button
            variant="outline"
            size="icon-sm"
            onClick={refetch}
            disabled={loading}
            aria-label="Refresh subscriptions"
          >
            <RefreshCw className={loading ? "animate-spin" : undefined} />
          </Button>
        </div>
      </div>

      {error && (
        <Card className="mb-5 border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-start gap-2 p-4">
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <div>
              <p className="text-[13px] font-medium text-destructive">
                Failed to load subscription data
              </p>
              <p className="mt-0.5 text-[12px] text-muted-foreground">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && <LoadingSkeleton />}

      {!loading && !error && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              label="Total Users"
              value={planDistribution?.total_users ?? 0}
              hint="All registered users"
              icon={Users}
            />
            <MetricCard
              label="Subscribed"
              value={planDistribution?.subscribed_users ?? 0}
              hint="Active paid plans"
              icon={UserCheck}
            />
            <MetricCard
              label="Trial Users"
              value={planDistribution?.trial_users ?? 0}
              hint="On active trial"
              icon={Crown}
            />
            <MetricCard
              label="Expired Trials"
              value={planDistribution?.expired_trial_users ?? 0}
              hint="Trial ended, not upgraded"
              icon={UserX}
            />
          </div>

          <SectionCard
            title="Plan Distribution"
            subtitle={
              planDistribution
                ? `${planDistribution.total_users} total users · ${planDistribution.subscribed_users} subscribed`
                : "Users grouped by plan"
            }
            icon={Crown}
          >
            {planData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={planData}
                  margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                >
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
                  <YAxis tick={AXIS_STYLE} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip content={ChartTooltip} cursor={{ fill: "var(--secondary)" }} />
                  <Bar dataKey="user_count" name="Users" radius={[3, 3, 0, 0]}>
                    {planData.map((plan, index) => (
                      <Cell
                        key={plan.plan}
                        fill={plan.is_paid ? T.primary : PLAN_BAR_COLORS[index % PLAN_BAR_COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState message="No plan data available." />
            )}
          </SectionCard>

          <SectionCard
            title="Plan Breakdown"
            subtitle="Per-plan user counts"
            icon={Users}
          >
            {planData.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plan</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Users</TableHead>
                    <TableHead className="text-right">Share</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {planData.map((plan) => {
                    const total = planDistribution?.total_users ?? 0;
                    const share = total > 0 ? ((plan.user_count / total) * 100).toFixed(1) : "0.0";
                    return (
                      <TableRow key={plan.plan}>
                        <TableCell className="font-mono">{plan.label}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {plan.is_paid ? "Paid" : "Free / Trial"}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums">
                          {plan.user_count}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                          {share}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <EmptyState message="No plan data available." />
            )}
          </SectionCard>
        </div>
      )}
    </div>
  );
}
